import { useCreateAccount } from '@my/directory-api/hooks';
import { setSupportSession } from '@my/platform/support';
import type { AppNameValue } from '@my/directory-api';
import {
  Button,
  ErrorBanner,
  FormSection,
  PageHeader,
  SelectInput,
  Switch,
  TextInput,
  showToast,
} from '@my/ui';
import { ArrowLeft, Copy, KeyRound } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

/**
 * Onboard a customer.
 *
 * <p>
 * NO PASSWORD FIELD, deliberately. The owner is invited and sets their own —
 * staff choosing a customer's credential would mean staff knowing it. The
 * account, its first organisation and the owner's membership are created
 * together; apps are enabled afterwards from the Subscriptions section, which
 * is what grants the owner a role in one.
 * </p>
 */
/** `Hub` is absent: it is universal, so there is no subscription to sell. */
const APPS: AppNameValue[] = ['Tms', 'Depot', 'Subsistence', 'Safety', 'Check'];
const PLANS = ['Trial', 'Basic', 'Professional', 'Enterprise'] as const;

export function CreateAccountPage() {
  const navigate = useNavigate();
  const create = useCreateAccount();

  const [accountName, setAccountName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerFirstName, setOwnerFirstName] = useState('');
  const [ownerLastName, setOwnerLastName] = useState('');
  const [appKey, setAppKey] = useState<AppNameValue | ''>('');
  const [plan, setPlan] = useState<(typeof PLANS)[number]>('Trial');
  const [issueTemporaryPassword, setIssueTemporaryPassword] = useState(false);

  // Shown once, after the fact. Held here rather than in a toast because it has
  // to be readable long enough to copy, and it can never be fetched again.
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  const canSubmit =
    accountName.trim().length > 0 && ownerEmail.trim().length > 0 && appKey !== '';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    create.mutate(
      {
        accountName: accountName.trim(),
        ownerEmail: ownerEmail.trim(),
        ownerFirstName: ownerFirstName.trim() || undefined,
        ownerLastName: ownerLastName.trim() || undefined,
        appKey: appKey as AppNameValue,
        plan,
        issueTemporaryPassword,
      },
      {
        onSuccess: (result) => {
          setSupportSession({
            accountId: result.accountId,
            accountName: accountName.trim(),
            organisationId: '',
            organisationName: '',
          });

          // A temporary password is the only thing on this screen that cannot
          // be recovered, so it stops here rather than navigating away from it.
          if (result.temporaryPassword) {
            setIssued({ email: ownerEmail.trim(), password: result.temporaryPassword });
            return;
          }

          // An existing login is not a failure, but it IS a different outcome:
          // no invite was needed and no password was issued, and saying
          // "invited to set a password" would send somebody looking for an
          // email that was never sent.
          showToast({
            type: 'success',
            message: result.ownerAlreadyExisted
              ? `${accountName.trim()} created with ${appKey}. ${ownerEmail.trim()} already had a login — their existing password still works.`
              : `${accountName.trim()} created with ${appKey}. ${ownerEmail.trim()} has been invited to set a password.`,
          });
          navigate('/account');
        },
      },
    );
  };

  return (
    <div className="flex h-full flex-col px-2">
      <div className="pt-3">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          All accounts
        </Link>
      </div>

      <PageHeader
        title="Create account"
        description="Sets up the account, its first organisation, and invites the owner."
      />

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {issued && (
          <FormSection title="Temporary password" columns={1}>
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">
                <strong>{accountName}</strong> was created. Sign in as{' '}
                <span className="font-mono text-xs">{issued.email}</span> with the password below.
              </p>

              <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                <KeyRound className="h-4 w-4 shrink-0 text-amber-700" aria-hidden />
                <code className="flex-1 select-all font-mono text-sm text-amber-900">
                  {issued.password}
                </code>
                <Button
                  type="button"
                  variant="secondary"
                  rightIcon={Copy}
                  onClick={() => {
                    void navigator.clipboard?.writeText(issued.password);
                    showToast({ type: 'success', message: 'Password copied.' });
                  }}
                >
                  Copy
                </Button>
              </div>

              <p className="text-xs text-slate-500">
                This is shown once and is stored nowhere — leaving this page loses it. The owner
                can change it from their profile, or reset it from the sign-in page.
              </p>

              <Button type="button" onClick={() => navigate('/account')}>
                Done
              </Button>
            </div>
          </FormSection>
        )}

        {!issued && create.error && (
          <ErrorBanner title="Could not create the account" message={create.error.message} />
        )}

        {!issued && (
        <form onSubmit={submit} className="flex flex-col gap-3">
          <FormSection title="Company" columns={2}>
            <TextInput
              label="Account name"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              helperText="Also names the first organisation. Both can be renamed later."
              required
            />
          </FormSection>

          <FormSection title="First app" columns={2}>
            <SelectInput
              label="App"
              placeholder="Choose an app…"
              options={APPS}
              value={appKey}
              onChange={(e) => setAppKey(e.target.value as AppNameValue)}
              helperText="Enabled straight away, and where the owner's invite sends them."
            />
            <SelectInput
              label="Plan"
              options={[...PLANS]}
              value={plan}
              onChange={(e) => setPlan(e.target.value as (typeof PLANS)[number])}
              helperText="A trial expires after 14 days."
            />
          </FormSection>

          {/*
            TESTING AID. Off by default and meant to stay that way for real
            customers: they should choose their own password and prove their own
            address, and neither fact should be known to whoever created the
            account. Removing this section and the flag it sets is all it takes
            to retire it.
          */}
          <FormSection title="Sign-in" columns={2}>
            <Switch
              label="Temporary password"
              enabledLabel="Issue one and skip the invite"
              disabledLabel="Email an invite (normal)"
              checked={issueTemporaryPassword}
              onChange={(e) => setIssueTemporaryPassword(e.target.checked)}
              errorMessage="For test accounts whose address receives nothing. Also marks the email verified, or every app would stop them at the verification screen. Ignored if the email already has a login — their password is never overwritten."
            />
          </FormSection>

          <FormSection title="Owner" columns={2}>
            <TextInput
              label="Email"
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              helperText="They are emailed a link to set their own password."
              required
            />
            <div />
            <TextInput
              label="First name"
              value={ownerFirstName}
              onChange={(e) => setOwnerFirstName(e.target.value)}
            />
            <TextInput
              label="Last name"
              value={ownerLastName}
              onChange={(e) => setOwnerLastName(e.target.value)}
            />
          </FormSection>

          <div className="flex items-center gap-2">
            <Button type="submit" disabled={!canSubmit || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create account'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => navigate('/')}>
              Cancel
            </Button>
          </div>
        </form>
        )}
      </main>
    </div>
  );
}
